import * as PIXI from 'pixi.js';
import { Howler } from 'howler';

import TouchControlsOverlay from './TouchControlsOverlay';
import GamePlayerUI from '../GamePlayerUI';
import Game, { ViewPortScaling } from '../Game';
import GameContext from '../GameContext';
// GameplayerUI imports
import store from '../GamePlayerUI/store';
import { setLives, setHealth, setTime, setScore, setAllPlayerInfo, setAllLevelGoalInfo, setViewDir } from '../GamePlayerUI/actions/gameInfoActions';
import { GameGoalFlag, GameScoreEnum } from '../Game/Enums';
import { openWithInfo, close } from '../GamePlayerUI/actions/levelPopupActions';
import { gameplayerUpdateLoadProgress } from '../GamePlayerUI/actions/loadingActions';
import ParticlesOverlay from './ParticlesOverlay';
import Vector from '../Game/Base/Math/Vector';
import Global from '../Game/Global';
import LevelPopupType from '../GamePlayerUI/components/UI/LevelPopup/Enums';

export default class GamePlayer extends GameContext {
    constructor(pixiApp, game, ui) {
        super(pixiApp, game, ui);
        if (!this.ui) this.ui = new GamePlayerUI();
        if (!this.game) this.game = new Game();

        // FOR DEBUG PURPOSES ONLY
        window.gamePlayer = this;

        GamePlayer.instance = this;
        window.GamePlayer = GamePlayer;

        this.pointsAtStartOfLevel = 0;
        this.points = 0;
        this.health = 100;
        this.time = 0;

        this.lives = 3;
        this.pointsGoal = -1;
        this.timeLimit = -1;

        this.running = false;

        this.particlesOverlay = new ParticlesOverlay(this, null);
        this.touchControlsOverlay = new TouchControlsOverlay(this, null);

        this.startValues = {};

        this.onFinished = null;

        this.closeLevelPopUpEvent = null;

        this.muted = false;
    }

    init(settingsPath, gameID, csrfToken) {
        this.ui.render();

        this.loader = this.game.init(settingsPath);
        this.loader._onUpdate = (progress, text) => {
            store.dispatch(gameplayerUpdateLoadProgress(Math.round(progress * 100), text));
        };

        this.gameID = gameID;
        this.csrfToken = csrfToken;

        if (this.gameID && this.gameID !== '') {
            this.loader.addStage('Game laden', 10, (finish, error) => {
                this.game.load(
                    this.gameID,
                    () => {
                        finish();
                    },
                    err => {
                        error(err);
                    }
                );
            });
        }

        PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR;

        this.initPixi('player-canvas-wrapper', 'player-canvas');

        this.loader.execute(
            () => {
                this.loaded();
            },
            error => {
                throw error;
            }
        );
    }

    loaded() {
        this.game.setViewPortScaling(ViewPortScaling.GamePlayBest);
        this.startPlay();

        window.requestAnimationFrame(this.render.bind(this));
    }

    onPlayerDeath() {
        this.lives--;
        if (this.game.goalSetting === GameGoalFlag.DIE) {
            this.game.activeLevel.finish('next');
        } else {
            this.game.activeLevel.finish('died');
        }
    }

    modifyPoints(difference, playAnimation = true) {
        this.points += difference;
        if (playAnimation) {
            if (difference > 0) {
                this.game.activeLevel.player.rewardAnimation.play();
                this.particlesOverlay.spawnParticle(
                    new Vector(
                        this.game.activeLevel.player.physicsBody.getPosition().x * Global.PHYSICS_SCALE,
                        this.game.activeLevel.player.physicsBody.getPosition().y * Global.PHYSICS_SCALE - Global.NATIVE_BLOCK_SIZE * 1.2
                    ),
                    'img.particle_coin',
                    `+${difference}`
                );
            } else {
                this.game.activeLevel.player.damageAnimation.play();
                this.particlesOverlay.spawnParticle(
                    new Vector(
                        this.game.activeLevel.player.physicsBody.getPosition().x * Global.PHYSICS_SCALE,
                        this.game.activeLevel.player.physicsBody.getPosition().y * Global.PHYSICS_SCALE - Global.NATIVE_BLOCK_SIZE * 1.2
                    ),
                    'img.particle_coin',
                    `${difference}`
                );
            }
        }
        if (this.game.goalSetting & GameGoalFlag.POINT_LIMIT) {
            if (this.points - this.startValues.points >= this.game.activeLevel.pointsGoal) {
                this.game.activeLevel.finish('next');
            }
        }
        store.dispatch(setScore(this.points));
    }

    modifyHealth(difference) {
        this.health += difference;
        if (difference > 0) {
            this.game.activeLevel.player.rewardAnimation.play();
            this.particlesOverlay.spawnParticle(
                new Vector(
                    this.game.activeLevel.player.physicsBody.getPosition().x * Global.PHYSICS_SCALE,
                    this.game.activeLevel.player.physicsBody.getPosition().y * Global.PHYSICS_SCALE - Global.NATIVE_BLOCK_SIZE * 1.2
                ),
                'img.particle_hearth',
                `+${difference}%`
            );
        } else {
            this.game.activeLevel.player.damageAnimation.play();
            this.game.activeLevel.player.damageColorAnimation.play();
            this.particlesOverlay.spawnParticle(
                new Vector(
                    this.game.activeLevel.player.physicsBody.getPosition().x * Global.PHYSICS_SCALE,
                    this.game.activeLevel.player.physicsBody.getPosition().y * Global.PHYSICS_SCALE - Global.NATIVE_BLOCK_SIZE * 1.2
                ),
                'img.particle_hearth',
                `${difference}%`
            );
        }
        if (this.health > 100) this.health = 100;
        if (this.health <= 0) {
            this.health = 0;
            this.onPlayerDeath();
        }
        store.dispatch(setHealth(this.health));
    }

    modifyTime(deltaTime) {
        const previousTimeRounded = Math.floor(this.time);
        this.time += deltaTime / 1000;
        if (this.game.goalSetting & GameGoalFlag.TIME_LIMIT) {
            // Check for time limit reached
            if (this.game.activeLevel.timeLimit <= this.time) {
                this.game.activeLevel.finish('time');
            }
        }
        if (Math.floor(this.time) !== previousTimeRounded) {
            // Only update every second
            if (this.game.scoreSetting === GameScoreEnum.FAST) {
                this.modifyPoints(-1, false);
            }
            if (this.game.scoreSetting === GameScoreEnum.SLOW) {
                this.modifyPoints(1, false);
            }
            store.dispatch(setTime(this.time));
        }
    }

    playSplashText(type) {
        document.getElementById(`splash-text-${type}`).classList.add('play');
        window.setTimeout(() => {
            document.getElementById(`splash-text-${type}`).classList.remove('play');
        }, 800);
    }

    playLivesIndicator(callback) {
        document.getElementById(`lives-indicator`).style.display = 'block';

        this.setTimeout(() => {
            document.getElementById(`lives-indicator-text`).classList.add('play');
            window.setTimeout(() => {
                document.getElementById(`lives-indicator-text`).classList.remove('play');
            }, 400);
            this.setTimeout(() => {
                store.dispatch(setLives(this.lives));
                this.setTimeout(() => {
                    document.getElementById(`lives-indicator`).style.display = 'none';
                    if (callback) callback();
                }, 200 + 500);
            }, 200);
        }, 500);
    }

    getPlaySessionValues() {
        return {
            points: this.points,
            health: this.health,
            lives: this.lives,
            time: this.time
        };
    }

    dispatchPlaySessionValues() {
        const allValues = this.getPlaySessionValues();
        store.dispatch(setAllPlayerInfo(allValues));
    }

    dispatchLevelGoalValues() {
        const allValues = {
            pointsGoal: this.pointsGoal,
            timeLimit: this.timeLimit,
            pointsAtStartOfLevel: this.pointsAtStartOfLevel
        };
        store.dispatch(setAllLevelGoalInfo(allValues));
    }

    resetPlaySessionValues() {
        this.lives = this.game.playerSettings.lives;
        this.points = 0;
        this.health = 100;
        this.time = 0;
        this.dispatchPlaySessionValues();
    }

    stopPlay() {
        this.resetPlaySessionValues();
        this.clearTimeout();
        this.closeLevelPopUp();
        this.touchControlsOverlay.detachFromStepParent();
        this.particlesOverlay.detachFromStepParent();
    }

    startPlay(onFinished = null, onGameOver = null) {
        const deathCallback = null;
        let gameOverCallback = onGameOver;
        let finishGameCallback = onFinished;
        if (gameOverCallback == null) {
            gameOverCallback = () => {
                this.openLevelPopUp(() => {
                    this.startPlay(finishGameCallback, gameOverCallback);
                }, LevelPopupType.GAMEOVER);
            };
        }
        if (finishGameCallback == null) {
            finishGameCallback = () => {
                this.openLevelPopUp(() => {
                    this.startPlay(finishGameCallback, gameOverCallback);
                }, LevelPopupType.FINISH);
            };
        }

        this.points = 0;
        this.resetPlaySessionValues();
        const finishCallback = upcomingLevel => {
            this.game.destroyBody();
            if (upcomingLevel) {
                this.openLevelPopUp(() => {
                    this.startLevel(upcomingLevel, finishCallback, null, gameOverCallback);
                }, LevelPopupType.NEXT);
            } else if (finishGameCallback) {
                finishGameCallback();
            }
        };
        this.startLevel(this.game.levels[0].uuid, finishCallback, null, gameOverCallback);
    }

    openLevelPopUp(callback, type = LevelPopupType.START) {
        const level = this.game.activeLevel;
        const levelIdx = this.game.levels.indexOf(this.game.activeLevel) + 1;
        const dispatchObj = {
            name: level.title,
            description: level.description,
            goals: this.game.goalSetting,
            levelIndex: levelIdx,
            onClose: (...args) => {
                if (type === LevelPopupType.START) window.removeEventListener('keydown', this.closeLevelPopUpEvent);
                callback(...args);
            },
            onRestart: (...args) => {
                callback(...args);
            }
        };
        store.dispatch(openWithInfo(type, dispatchObj));

        this.closeLevelPopUpEvent = evt => {
            if (evt.key === ' ') {
                this.closeLevelPopUp();
                callback();
                window.removeEventListener('keydown', this.closeLevelPopUpEvent);
            }
        };
        if (type === LevelPopupType.START) window.addEventListener('keydown', this.closeLevelPopUpEvent);
    }

    closeLevelPopUp() {
        window.removeEventListener('keydown', this.closeLevelPopUpEvent);
        store.dispatch(close());
    }

    startLevel(levelUUID, onFinished, onDeath, onGameOver) {
        if (onDeath === null) {
            onDeath = () => {
                this.game.destroyBody();
                this.openLevelPopUp(() => {
                    this.startLevel(levelUUID, onFinished, onDeath, onGameOver);
                }, LevelPopupType.ALMOST);
            };
        }

        this.particlesOverlay.detachFromStepParent();
        this.startValues = this.getPlaySessionValues();
        const level = this.game.levels.findByUUID(levelUUID);
        if (!level) throw new Error('Failed to start level. Error with given UUID cannot be found.');
        this.game.levels.setActive(this.game.levels.indexOf(level), false, false);
        this.game.activeLevel = level;
        this.health = 100;
        this.time = 0;
        const gamePlayer = this;

        const viewDir = level.viewDirection;
        store.dispatch(setViewDir(viewDir));

        level.onFinished = upcomingLevel => {
            gamePlayer.running = false;
            if (this.lives === 0) {
                this.playLivesIndicator(() => {
                    this.playSplashText('game-over');
                    gamePlayer.setTimeout(() => {
                        this.resetPlaySessionValues();
                        this.particlesOverlay.detachFromStepParent();
                        if (onGameOver) {
                            onGameOver();
                        } else {
                            this.startLevel(levelUUID, onFinished, onDeath, onGameOver);
                        }
                    }, 800);
                });
            } else if (upcomingLevel === 'died' || upcomingLevel === 'time') {
                this.playSplashText('level-over');
                gamePlayer.setTimeout(() => {
                    this.playLivesIndicator(() => {
                        this.points = this.startValues.points;
                        this.health = this.startValues.health;
                        this.particlesOverlay.detachFromStepParent();
                        if (onDeath) {
                            onDeath();
                        }
                    });
                }, 800);
            } else {
                this.playSplashText('finish');
                gamePlayer.setTimeout(() => {
                    this.particlesOverlay.detachFromStepParent();
                    if (onFinished) onFinished(upcomingLevel);
                }, 800);
            }
        };
        // Set level goal(s)
        this.pointsGoal = this.game.goalSetting & GameGoalFlag.POINT_LIMIT ? level.pointsGoal : -1;
        this.timeLimit = this.game.goalSetting & GameGoalFlag.TIME_LIMIT ? level.timeLimit : -1;
        // Initialize game
        this.pointsAtStartOfLevel = this.points;
        this.game.destroyBody();
        this.game.destroyVisuals();
        this.game._gsoSetPlayMode(true);
        this.game.createVisuals();
        this.game.updateRenderSize();
        this.touchControlsOverlay.attachToStepParent(this.game);
        // Dispatch session values
        this.dispatchPlaySessionValues();
        this.dispatchLevelGoalValues();

        this.particlesOverlay.attachToStepParent(level);

        if (document.getElementById(`lives-indicator`)) document.getElementById(`lives-indicator`).style.display = 'none';

        this.openLevelPopUp(() => {
            // Start countdown
            gamePlayer.playSplashText('3');
            gamePlayer.setTimeout(() => {
                gamePlayer.playSplashText('2');
                gamePlayer.setTimeout(() => {
                    gamePlayer.playSplashText('1');
                    gamePlayer.setTimeout(() => {
                        gamePlayer.playSplashText('start');
                        gamePlayer.setTimeout(() => {
                            gamePlayer.game.paused = false;
                            gamePlayer.running = true;
                            gamePlayer.game.createBody();
                        }, 800);
                    }, 800);
                }, 800);
            }, 800);
        });
    }

    moveRenderer() {
        document.getElementById('player-canvas-wrapper').appendChild(this.pixiApp.view);
    }

    isGamePaused() {
        return this.game.paused;
    }

    togglePause() {
        if (this.game._hasBody) this.game.paused = !this.game.paused;
    }

    isGameMuted() {
        return this.muted;
    }

    toggleMute() {
        this.muted = !this.muted;
        Howler.mute(this.muted);
    }
}
GamePlayer.instance = null;

export function GetGamePlayerInstance() {
    return GamePlayer.instance;
}
